import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { CloneReportNotice, isCloneReport } from "@/components/instructor/clone-report-notice";
import { CourseBuilder } from "@/components/instructor/course-builder";
import { useAuthStore } from "@/stores/authStore";
import {
  fetchCourseBuilder,
  fetchStaffCoursePrices,
  staffPricesToForm,
} from "@/services/api/instructor";

export function TenantInstructorCourseEditorPage() {
  const { tenantSlug = "", courseId = "" } = useParams<{ tenantSlug: string; courseId: string }>();
  const t = useTranslations("instructor");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const cloneReport = (useLocation().state as { cloneReport?: unknown } | null)?.cloneReport;

  const editorQuery = useQuery({
    queryKey: ["instructor-course-builder", courseId],
    queryFn: async () => {
      const [builder, prices] = await Promise.all([
        fetchCourseBuilder(courseId),
        fetchStaffCoursePrices(courseId),
      ]);
      return { builder, initialPrices: staffPricesToForm(prices) };
    },
    enabled: isAuthenticated && isStaffRole(user?.role) && Boolean(courseId),
  });

  if (!isAuthenticated) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (!isStaffRole(user?.role)) {
    return <Navigate to={`/t/${tenantSlug}/dashboard`} replace />;
  }

  if (editorQuery.isLoading) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </main>
    );
  }

  if (editorQuery.isError || !editorQuery.data) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-10">
        <p className="text-sm text-[var(--text-muted)]">{t("courseNotFound")}</p>
      </main>
    );
  }

  const { builder, initialPrices } = editorQuery.data;

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Link
        to={`/t/${tenantSlug}/instructor/courses`}
        className="text-sm text-[var(--text-muted)] hover:underline"
      >
        {t("backToAllCourses")}
      </Link>
      <div className="mt-2 flex flex-wrap gap-4">
        <Link
          to={`/t/${tenantSlug}/instructor/courses/${courseId}/gradebook`}
          className="text-sm text-[var(--text-heading)] underline"
          data-testid="course-gradebook-link"
        >
          {t("openGradebook")}
        </Link>
        <Link
          to={`/t/${tenantSlug}/instructor/courses/${courseId}/analytics`}
          className="text-sm text-[var(--text-heading)] underline"
          data-testid="course-analytics-link"
        >
          {t("openAnalytics")}
        </Link>
      </div>
      {isCloneReport(cloneReport) ? (
        <div className="mt-4">
          <CloneReportNotice report={cloneReport} />
        </div>
      ) : null}
      <div className="mt-4">
        <CourseBuilder
          tenantSlug={tenantSlug}
          courseId={courseId}
          initialCourse={builder.course}
          initialModules={builder.modules}
          initialPrices={initialPrices}
          capabilities={builder.capabilities}
        />
      </div>
    </main>
  );
}
